// Prompt-engineered ReAct protocol for BabyAI tool calling. Ported
// from Agicore Foundry where Christopher iterated through three failure
// modes (Sprint 7g → b1e6f86 → de2fe03):
//
//   1. OpenAI native `tools` field — BabyAI Space accepts but doesn't
//      surface `tool_calls`, models silently degrade to plain text.
//   2. Single ReAct directive in the middle of the prompt — Llama 3.1
//      8B loses the directive across context and "explain-modes" into
//      markdown fences.
//   3. (the fix) Hard banner at the TOP, anti-instructions, complete
//      worked example, reinforcement footer at the BOTTOM.
//
// The protocol: model emits <playground_tool name="X">{json args}</...>
// blocks. Runtime parses, executes, returns <tool_result> blocks as
// the next user turn. Loop until a turn has no tool blocks.

import { getTools } from "./tools";

export const TOOL_BLOCK_RE =
  /<playground_tool\s+name="([^"]+)"\s*>([\s\S]*?)<\/playground_tool>/g;

export const MAX_AGENT_TURNS = 6;

/** The system prompt's tool-using head. Front-loaded so the model
 *  weights the directive over later context. */
export function reactHead(): string {
  return `# ★★★ YOU CALL TOOLS. YOU DO NOT EXPLAIN HOW TO CALL TOOLS. ★★★

You are the agent in a BabyAI Playground channel — a shared team chat. When the user asks for something you can answer with a tool, you EMIT TOOL BLOCKS that the runtime executes for you. You do not write "use the fetch_url tool" — YOU use it.

# How to call a tool

Emit a block in EXACTLY this shape (no markdown fences around it, no commentary inside it):

<playground_tool name="fetch_url">
{"url": "https://example.com"}
</playground_tool>

Rules:
- Args MUST be valid JSON between the tags.
- Inside JSON strings: escape newlines as \\n and quotes as \\".
- After your block, STOP. The runtime will execute the tool and reply with a <tool_result> message in the next turn. Wait for it.
- You MAY emit multiple <playground_tool> blocks in one turn — they all execute and you get all the results back at once.
- When you have the answer, send ONE plain-text message with NO <playground_tool> blocks. That ends the turn and shows the answer to the channel.

# Available tools

${getTools().map((t) => `${t.name}\n  ${t.description}\n  args: ${t.argsDoc}`).join("\n\n")}

# DO NOT

- DO NOT wrap tool calls in markdown code fences (no \`\`\`xml or \`\`\`json blocks).
- DO NOT tell the user "I'll use the fetch_url tool" — just use it.
- DO NOT put prose commentary inside the <playground_tool> block — only valid JSON.
- DO NOT narrate "I'm about to call a tool now." Just call it.

# Complete worked example

User: what time is it in tokyo right now?

You (one turn):
<playground_tool name="current_time">
{"tz": "Asia/Tokyo"}
</playground_tool>

Runtime (next turn, sent to you):
<tool_result tool="current_time" ok="true">
Jun 25, 2026, 02:34:56 PM JST  (Asia/Tokyo)
</tool_result>

You (next turn, to the channel):
It's 2:34 PM in Tokyo right now.

# Style

Be concise. The channel is shared — every message is visible to everyone. Multi-step asks deserve a brief checklist before you start. After the tools succeed, give the answer in plain prose, no scaffolding.
`;
}

/** Short reinforcement appended at the end of the system prompt — in
 *  case a long conversation history caused the model to drift from the
 *  directive. Restates the protocol in one paragraph. */
export const REACT_TAIL = `

# Reminder

Tool calls go in <playground_tool name="..."> blocks with valid JSON between the tags. DO NOT use markdown code fences for tool calls. DO NOT narrate the tool spec back to the user. After your tool block, stop and wait for <tool_result>. When everything succeeds, end with ONE plain-text message — no tool blocks.
`;

export interface ParsedToolCall {
  name: string;
  rawArgs: string;
}

/** Pull all <playground_tool> blocks out of model output. */
export function parseToolCalls(text: string): ParsedToolCall[] {
  const calls: ParsedToolCall[] = [];
  let m: RegExpExecArray | null;
  TOOL_BLOCK_RE.lastIndex = 0;
  while ((m = TOOL_BLOCK_RE.exec(text)) !== null) {
    calls.push({ name: m[1], rawArgs: m[2].trim() });
  }
  TOOL_BLOCK_RE.lastIndex = 0;
  return calls;
}

/** Strip <playground_tool> blocks from text so the user-facing chat
 *  message doesn't show the raw XML. */
export function stripToolBlocks(text: string): string {
  return text.replace(TOOL_BLOCK_RE, "").trim();
}

export function formatToolResultBlock(name: string, ok: boolean, detail: string): string {
  return `<tool_result tool="${name}" ok="${ok}">\n${detail}\n</tool_result>`;
}
